import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
	documentsPreviewQuery,
	useAxiosInstance,
	usePoQuery,
} from '@gov-nx/api';
import { useMessageEvents } from '@gov-nx/core/events';
import { ownDocumentKeys } from './queryKeys';

interface DocumentPreviewHookProps {
	documentId?: number;
	souborId?: string;
	onError?: () => void;
}

interface ProcessingData {
	documentId: number;
	souborId: string;
	onSuccess?: (data: Blob) => void;
}

export const useDocumentPreview = (props?: DocumentPreviewHookProps) => {
	const [processing, setProcessing] = useState<ProcessingData | undefined>(
		undefined
	);
	const queryClient = useQueryClient();
	const { toastMessageError } = useMessageEvents();
	const { axiosInstance } = useAxiosInstance();

	const documentPreviewQuery = usePoQuery({
		queryKey: ownDocumentKeys().preview(
			processing?.documentId,
			processing?.souborId
		),
		queryFn: async () =>
			processing?.documentId
				? documentsPreviewQuery(axiosInstance)(
						processing.documentId,
						processing.souborId
					)
				: undefined,
		enabled: !!processing?.documentId,
		refetchOnWindowFocus: false,
		cacheTime: 0,
	});

	useEffect(() => {
		if (props?.documentId && props?.souborId) {
			setProcessing({
				documentId: props.documentId,
				souborId: props.souborId,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
	}, []);

	useEffect(() => {
		if (documentPreviewQuery.isError) {
			props?.onError && props.onError();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
	}, [documentPreviewQuery.isError, toastMessageError]);

	useEffect(() => {
		if (documentPreviewQuery.isFetched) {
			if (processing?.onSuccess && documentPreviewQuery.data) {
				processing?.onSuccess(documentPreviewQuery.data);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
	}, [documentPreviewQuery.isFetched]);

	return {
		documentPreviewQuery,
		downloadingId: processing?.souborId,
		fetchFile: (
			documentId: number,
			souborId: string,
			onSuccess: (file: Blob) => void
		) => {
			const cached = queryClient.getQueryData<Blob>(
				ownDocumentKeys().preview(documentId, souborId)
			);

			if (cached) {
				onSuccess(cached);
			}

			setProcessing({ documentId: documentId, souborId: souborId, onSuccess });
		},
	};
};
